import { Draw } from "../../models/Draw";
import {
  CURRENCY,
  isMiniApp,
  MAXIMUM_INSTITUTION_LENGTH,
  SECONDS_PER_SLOT_ANIMATION,
  TICKETS_BOUGHT_IN_DRAW_DIALOG,
  WIN_RATIO_SPREAD,
} from "../../utils/Constants";
import Headline from "../miscellaneous/Headline";
import TicketEntry from "../miscellaneous/TicketEntry";
import info_gold from "../../assets/icons/info_gold.svg";
import info_gray from "../../assets/icons/info_gray.svg";
import { useEffect, useState } from "react";
import chevron_down from "../../assets/icons/chevron_down.svg";
import chevron_up from "../../assets/icons/chevron_up.svg";
import Button from "../miscellaneous/Button";
import { formatDate, shareMiniApp } from "../../utils/Utils";
import Slot from "../miscellaneous/Slot";
import SlotCounter from "react-slot-counter";
import { usePlayerData } from "../../hooks/usePlayerData";
import Dialog from "./components/Dialog";
import { ButtonTheme } from "../miscellaneous/ButtonTheme";
import { Address } from "viem";
import ShareDialog from "./ShareDialog";
import { useDialog } from "../../hooks/useDialog";
import { FaShareAlt } from "react-icons/fa";

export interface DrawDialogProps {
  onClose: () => void;
  draw: Draw;
  address?: Address;
  announce: boolean;
}

export default function DrawDialog(props: DrawDialogProps) {
  /**
   * Hooks
   */
  const { address } = usePlayerData();
  const [showingMore, setShowingMore] = useState<boolean>(false);
  const [announcing, setAnnouncing] = useState<boolean | undefined>(undefined);
  const { openDialog, closeDialog } = useDialog();

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (props.announce) {
      setAnnouncing(true);
      timer = setTimeout(
        () => {
          setAnnouncing(false);
        },
        SECONDS_PER_SLOT_ANIMATION *
          1000 *
          (props.draw.ticket.mainNumbers.length +
            props.draw.ticket.luckyStars.length +
            SECONDS_PER_SLOT_ANIMATION / 2),
      );
    } else {
      clearTimeout(timer);
      setAnnouncing(undefined);
    }

    // Cleanup the timer if `announce` changes or the component unmounts
    return () => clearTimeout(timer);
  }, [
    props.announce,
    props.draw.ticket.luckyStars.length,
    props.draw.ticket.mainNumbers.length,
  ]);

  /**
   * Execute on close
   */
  const onClose = () => {
    setShowingMore(false);
    setAnnouncing(undefined);
    props.onClose();
  };

  let color: string =
    "bg-gradient-to-b bg-clip-text text-transparent from-white to-emperor";
  let text: string = "ROLLOVER";
  let tooltip: string | undefined;
  let tooltipIcon: string | undefined;
  if (props.address == undefined || props.draw.ticketsBought.length == 0) {
    if (props.draw.rollover) {
      tooltip =
        "Rollover means the Jackpot wasn't won by any of the participants, and it will carry over to the next draw.";
      tooltipIcon = info_gray;
    } else {
      color =
        "bg-gradient-to-b bg-clip-text text-transparent from-mango to-crail";
      text = "SOMEONE HIT THE JACKPOT!";
      tooltip =
        "This means the Jackpot was won by one or more participants, and it will be distributed amongst them.";
      tooltipIcon = info_gold;
    }
  } else {
    if (props.draw.userPrize != undefined && props.draw.userPrize.value > 0n) {
      if (props.draw.userPrize.value >= props.draw.jackpot.value) {
        color =
          "bg-gradient-to-b bg-clip-text text-transparent from-mango to-crail";
        text = "YOU HIT THE JACKPOT!";
        tooltip = "This means you won the maximum prize.";
        tooltipIcon = info_gold;
      } else {
        color =
          "bg-gradient-to-b bg-clip-text text-transparent from-mango to-crail";
        text = "NOT THE JACKPOT, BUT STILL A WIN!";
      }
    } else {
      text = "BETTER LUCK NEXT TIME!";
    }
  }
  return (
    <Dialog
      dismissible={!announcing}
      onClose={onClose}
      shouldScroll={!(announcing ?? props.announce)}
    >
      <span className="flex items-center justify-center gap-2 text-center ~text-xl/xxl ~leading-5/6 font-archb font-bold bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
        DRAW #{props.draw.number.toString()}
      </span>
      <span className="font-poppins font-bold ~text-sm/base text-gray ~mt-1/2 ~mb-2/3">
        {formatDate(props.draw.date)}
      </span>
      <div className="flex flex-col items-center">
        <div className="~w-56/72">
          <Headline value={"DRAW"}></Headline>
        </div>
        <div className="~mt-0.5/1"></div>
        <div className="select-none flex flex-row">
          {props.draw?.ticket.mainNumbers.map((mainNumber, index) => (
            <Slot key={index} isStar={false}>
              <SlotCounter
                key={index}
                value={Number(mainNumber).toString().padStart(2, "0")}
                duration={
                  props.announce
                    ? SECONDS_PER_SLOT_ANIMATION * index +
                      SECONDS_PER_SLOT_ANIMATION
                    : 0
                }
              />
            </Slot>
          ))}
          {props.draw?.ticket.luckyStars.map((luckyStar, index) => (
            <Slot key={index} isStar={true}>
              <SlotCounter
                key={index}
                value={Number(luckyStar).toString().padStart(2, "0")}
                duration={
                  props.announce
                    ? SECONDS_PER_SLOT_ANIMATION *
                        Number(props.draw?.lotteryConfig.numbersPerTicket) +
                      SECONDS_PER_SLOT_ANIMATION * index +
                      SECONDS_PER_SLOT_ANIMATION
                    : 0
                }
              />
            </Slot>
          ))}
        </div>
      </div>
      <div
        className={`flex flex-col items-center transform transition-all duration-1000 ${!(announcing ?? props.announce) ? "h-96 scale-100 opacity-100" : "h-0 scale-50 opacity-0"}`}
      >
        <div className="flex flex-col items-center ~mb-3/4">
          <div className="flex flex-row">
            <div className="flex flex-col items-start ~mt-2/3">
              <p className="font-barlowc font-bold ~text-sm/base bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
                JACKPOT
              </p>
              <div className="flex flex-row">
                <p className="font-archb font-bold ~text-xxl/xxxl ~leading-6/8 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                  {props.draw.jackpot.pretty}
                </p>
                <p className="font-archb font-bold ~text-xxl/xxxl ~leading-6/8 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                  &nbsp;{CURRENCY}
                </p>
              </div>
            </div>
            {props.draw.charity != undefined && (
              <div className="~mt-2/3 ~ml-6/10 flex flex-col items-start">
                <div className="flex flex-row items-end">
                  <p className="font-barlowc font-bold ~text-sm/base bg-gradient-to-b from-bermuda to-ming bg-clip-text text-transparent">
                    RAISED FOR&nbsp;
                  </p>
                  <p
                    className="cursor-pointer underline font-barlowc font-bold ~text-sm/base text-bermuda"
                    onClick={() =>
                      window.open(props.draw.charity!.website, "_blank")
                    }
                  >
                    {props.draw.charity.name
                      .substring(0, MAXIMUM_INSTITUTION_LENGTH)
                      .toUpperCase()}
                    {props.draw.charity.name.length > MAXIMUM_INSTITUTION_LENGTH
                      ? "..."
                      : ""}
                  </p>
                </div>
                <div className="flex flex-row">
                  <p
                    className={`font-archb font-bold ~text-xxl/xxxl ~leading-6/8 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor`}
                  >
                    {props.draw.raised?.pretty ?? "..."}
                  </p>
                  <p className="font-archb font-bold ~text-xxl/xxxl ~leading-6/8 bg-clip-text text-transparent bg-gradient-to-b from-bermuda to-ming">
                    &nbsp;{CURRENCY}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {address != undefined &&
          props.draw.ticketsBought.length > 0 &&
          props.draw.userPrize != undefined && (
            <div className="flex flex-col items-center ~mb-1/2">
              <div className="~w-56/72">
                <Headline
                  value={
                    "YOUR ENTRIES (" + props.draw.ticketsBought.length + ")"
                  }
                ></Headline>
              </div>
              <div className="flex flex-col items-stretch ~mt-0.5/1">
                {props.draw.ticketsBought
                  .slice(0, showingMore ? props.draw.ticketsBought.length : 5)
                  .map((ticket, index) => (
                    <div key={index} className="flex flex-row">
                      <TicketEntry
                        key={index}
                        ticket={ticket}
                        drawTicket={props.draw.ticket}
                      ></TicketEntry>
                      <div className="flex flex-row items-center ~ml-1.5/2.5">
                        <p className="font-archb font-bold ~text-base/xl ~leading-4/6 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                          = {ticket.prize?.pretty ?? "..."}
                        </p>
                        <p className="font-archb font-bold ~text-base/xl ~leading-4/6 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                          &nbsp;{CURRENCY}
                        </p>
                      </div>
                    </div>
                  ))}
                {props.draw.ticketsBought.length == 0 && (
                  <p className="font-poppins text-gray ~text-sm/base">
                    Nothing to see here.
                  </p>
                )}
                {props.draw.ticketsBought.length >
                  TICKETS_BOUGHT_IN_DRAW_DIALOG && (
                  <button
                    className="self-center flex flex-row items-center ~py-0.5/1 hover:scale-105 duration-250"
                    onClick={() => setShowingMore(!showingMore)}
                  >
                    <div className="font-archb ~text-xxs/xs ~leading-5/6 bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
                      SEE {showingMore ? "LESS" : "MORE"}
                    </div>
                    <img
                      key={showingMore ? "chevron_up" : "chevron_down"}
                      src={showingMore ? chevron_up : chevron_down}
                      className="~pl-1/1.5 ~h-1/1.5"
                      alt="chevron"
                    />
                  </button>
                )}
              </div>
              <div className="flex flex-row items-center">
                <div className="flex flex-col items-start ~mt-1/2">
                  <p className="font-barlowc font-bold ~text-xs/sm bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
                    TOTAL
                  </p>
                  <div className="flex flex-row items-center">
                    <p className="font-archb font-bold ~text-base/xl ~leading-4/6 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                      {props.draw.userPrize.pretty}
                    </p>
                    <p className="font-archb font-bold ~text-base/xl ~leading-4/6 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                      &nbsp;{CURRENCY}
                    </p>
                  </div>
                </div>
                <div
                  className={`ml-4 ~px-1/2 ~py-1/2 cursor-pointer flex flex-row items-center ~rounded-sm/md drop-shadow-lg hover:scale-105 duration-250 bg-gradient-to-b hover:bg-gradient-to-t ${ButtonTheme.PersianPink}`}
                  onClick={() => {
                    if (isMiniApp) {
                      shareMiniApp(
                        `I earned ${props.draw.userPrize} in prizes from this BitMillions' draw 🚀`,
                        window.location.href,
                      );
                    } else {
                      openDialog(
                        ShareDialog({
                          onClose: closeDialog,
                          earnings: props.draw.userPrize!,
                        }),
                      );
                    }
                  }}
                >
                  <span className="self-center text-cinder font-barlowc font-bold mr-2 leading-1 ~text-sm/base">
                    SHARE
                  </span>
                  <FaShareAlt className="self-center ~text-xs/sm text-cinder"></FaShareAlt>
                </div>
              </div>
            </div>
          )}
        <div className="flex flex-row items-center ~mb-3/4">
          <span
            className={
              "self-end font-archb ~text-xxs/sm ~leading-2.5/4 " +
              (props.draw.rollover ? "text-gray" : color)
            }
          >
            {text}
          </span>
          {tooltip && (
            <img
              src={tooltipIcon}
              className="select-none ~pl-1/1.5 ~h-3/4"
              alt="info"
              data-tooltip-id="default-tooltip"
              data-tooltip-content={tooltip}
            />
          )}
        </div>
        {props.draw.entries >= 0 && (
          <div className="~w-56/72">
            <Headline value={"OTHER STATISTICS"}></Headline>
          </div>
        )}
        {props.draw.entries >= 0 && (
          <div className="flex flex-col w-[56%] ~mt-0.5/1 ~mb-3/4">
            <div className="flex flex-row justify-between w-full ~mt-0.5/1">
              <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-br from-persian-pink to-cadillac">
                ENTRIES
              </p>
              <p className="font-archb ~text-xs/sm text-silver">
                {props.draw.entries.toString()}
              </p>
            </div>
            <div className="flex flex-row justify-between w-full ~mt-0.5/1">
              <p className="font-archb ~text-xs/sm text-gray">TOTAL WINNERS</p>
              <p className="font-archb ~text-xs/sm text-silver">
                {props.draw.totalWinners}
              </p>
            </div>
            <div className="flex flex-row justify-between w-full ~mt-0.5/1">
              <p className="font-archb ~text-xs/sm text-gray">WIN RATIO</p>
              <p className="font-archb ~text-xs/sm text-silver">
                {Math.ceil(props.draw.winRatio * WIN_RATIO_SPREAD)} in{" "}
                {WIN_RATIO_SPREAD}
              </p>
            </div>
          </div>
        )}
        <div className="flex flex-col items-center">
          <div className="~w-56/72">
            <Headline value={"PRIZE DISTRIBUTION"}></Headline>
          </div>
          <div className="~mt-1/2 w-fit overflow-scroll">
            <table className=" bg-cinder text-silver border-collapse border-2 border-bastille">
              <tbody>
                <tr className="bg-bastille">
                  {[
                    "Numbers Matched",
                    "Prize per Winner",
                    "Prize Fund Amount",
                    "Total Winners",
                  ].map((prizeTier, index) => (
                    <th
                      key={index}
                      className="border-bastille border-collapse border-2 ~text-sm/base font-poppins font-normal ~px-2/3"
                    >
                      {prizeTier}
                    </th>
                  ))}
                </tr>
                {props.draw.prizeTiersResults.map((prizeTier, index) => (
                  <tr key={index}>
                    <td className="border-collapse border-2 border-bastille">
                      <div className="flex flex-row float-left items-center ~mx-1.5/2.5 ~my-1/1.5">
                        <p className="font-archb font-bold ~text-base/lg leading-5 text-silver">
                          {Number(prizeTier.combination.mainNumbers)}&nbsp;+
                        </p>
                        <p className="font-archb font-bold ~text-base/lg leading-5 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                          &nbsp;{Number(prizeTier.combination.luckyStars)}
                        </p>
                      </div>
                    </td>
                    <td className="border-collapse border-2 border-bastille">
                      <div className="flex flex-row float-right items-center ~mx-1.5/2.5">
                        <p className="font-archb font-bold ~text-sm/base leading-4 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                          {prizeTier.prizePerWinner.pretty}
                        </p>
                        <p className="font-archb font-bold ~text-sm/base leading-4 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                          &nbsp;{CURRENCY}
                        </p>
                      </div>
                    </td>
                    <td className="border-collapse border-2 border-bastille text-end">
                      <div className="flex flex-row float-right items-center ~mx-1.5/2.5">
                        <p className="font-archb font-bold ~text-sm/base leading-4 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                          {prizeTier.prizeFundAmount.pretty}
                        </p>
                        <p className="font-archb font-bold ~text-sm/base leading-4 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                          &nbsp;{CURRENCY}
                        </p>
                      </div>
                    </td>
                    <td className="border-collapse border-2 border-bastille text-end">
                      <p className="float-right font-archb font-bold ~text-sm/base leading-4 text-silver ~mx-1.5/2.5">
                        {Number(prizeTier.totalWinners)}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="self-center ~mt-2/3">
          <Button
            text={"CLOSE"}
            enabled={true}
            loading={false}
            theme={ButtonTheme.PersianPink}
            onClick={onClose}
          />
        </div>
        <div className="select-none self-center ~mt-1/2">
          <span>&nbsp;&nbsp;</span>
        </div>
      </div>
    </Dialog>
  );
}
